import {
  createEntityAdapter,
  type EntityAdapter,
  type EntityState,
  type ReducerCreators,
  type SerializedError,
  type ThunkDispatch,
  type UnknownAction,
} from '@reduxjs/toolkit';

import type { Logger } from '../../utils/core';
import { getId } from './resourceStoreTypes';

export interface QcsResource {
  clientId?: string;
  id?: string;
  createdAt?: string;
}
export interface QcsResourceSlice<T extends QcsResource = QcsResource> extends EntityState<T, string> {
  loadPending?: boolean;
  isLoading?: boolean;
  error?: SerializedError | null;
  lastSyncDate?: string | null;
  // Set only by qcsResourceCacheToState: the entities came from a completed fetch in an earlier session.
  hasCachedResult?: boolean;
  // Update/delete requests still in flight, and whether one failed since the last completed fetch. Either way the
  // entities hold optimistic changes the server has not confirmed.
  pendingMutations?: number;
  hasRejectedMutation?: boolean;
}

export const commonSelectors = {
  getLoadPending: (s: QcsResourceSlice) => s.loadPending,
  getIsLoading: (s: QcsResourceSlice) => s.isLoading,
  getError: (s: QcsResourceSlice) => s.error,
  getHasCachedResult: (s: QcsResourceSlice) => Boolean(s.hasCachedResult),
};

/** Persists only server-confirmed entities from a completed fetch (this session's or a restored one). */
export function qcsResourceStateToCache<T extends QcsResource>(
  state: QcsResourceSlice<T>,
): Pick<QcsResourceSlice<T>, 'ids' | 'entities'> | null {
  if (state.loadPending && !state.hasCachedResult) return null;
  const ids = state.ids.filter((id) => state.entities[id]?.id === id);
  return { ids, entities: Object.fromEntries(ids.map((id) => [id, state.entities[id]])) };
}

/**
 * Skips cache writes while entities hold unconfirmed update/delete changes, so the stored cache keeps the last
 * server-confirmed snapshot even when a mutation and its recovery fetch both fail.
 */
export function qcsResourceShouldPersist<T extends QcsResource>(
  lastState: QcsResourceSlice<T> | undefined,
  nextState: QcsResourceSlice<T> | undefined,
): boolean {
  if (!nextState || lastState === nextState) return false;
  return !nextState.pendingMutations && !nextState.hasRejectedMutation;
}

/**
 * Restores cached entities as still pending, so fetch-once callers refresh them as before; UI can
 * use getHasCachedResult to show the cached result instead of a first-load skeleton.
 */
export const createQcsResourceCacheToState =
  <T extends QcsResource>(adapter: EntityAdapter<T, string>, initialState: QcsResourceSlice<T>) =>
  (cache: unknown): QcsResourceSlice<T> => {
    const cached = cache as Partial<Pick<QcsResourceSlice<T>, 'ids' | 'entities'>> | null;
    if (!cached || !Array.isArray(cached.ids) || !cached.entities) return initialState;
    const entities = cached.ids.map((id) => cached.entities?.[id]).filter((entity): entity is T => Boolean(entity));
    return { ...adapter.setAll(initialState, entities), loadPending: true, isLoading: false, hasCachedResult: true };
  };

interface QcsApi<T, RemoteT = T & { id: string; clientId?: string }> {
  create(item: T, options?: { signal?: AbortSignal }): Promise<{ resource: RemoteT; clientId?: string; id: string }>;
  update(item: Partial<T> & { id: string }): Promise<{ resource: RemoteT; id: string }>;
  delete(id: string): Promise<void>;
  fetch(): Promise<ReadonlyArray<RemoteT>>;
}

const startMutation = (state: QcsResourceSlice) => {
  state.pendingMutations = (state.pendingMutations ?? 0) + 1;
};
const settleMutation = (state: QcsResourceSlice) => {
  state.pendingMutations = Math.max(0, (state.pendingMutations ?? 0) - 1);
};

export const createResourceEntityAdapter = <T extends QcsResource>() =>
  createEntityAdapter<T, string>({
    selectId: getId,
    sortComparer: (a, b) => ((a.createdAt ?? '') > (b.createdAt ?? '') ? 1 : -1),
  });

export const createQcsThunks = <T extends QcsResource>({
  create,
  api,
  adapter,
  log,
  refetch,
}: {
  create: ReducerCreators<QcsResourceSlice<T>>;
  api: QcsApi<T>;
  adapter: EntityAdapter<T, string>;
  log?: Pick<Logger, 'debug' | 'error'>;
  refetch: (dispatch: ThunkDispatch<unknown, unknown, UnknownAction>) => void;
}) => ({
  create: create.asyncThunk(
    async (item: T & { clientId: string }, thunkApi) => {
      log?.debug('Creating item', item);
      try {
        return await api.create(item, { signal: thunkApi.signal });
      } catch (e) {
        if (!thunkApi.signal.aborted) {
          refetch(thunkApi.dispatch);
        }
        throw e;
      }
    },
    {
      pending(state, action) {
        state.isLoading = true;
        adapter.addOne(state, action.meta.arg);
      },
      rejected(state, action) {
        if (action.meta.aborted) {
          adapter.removeOne(state, getId(action.meta.arg));
          return;
        }
        log?.error('Received error', action.error);
        state.error = action.error;
      },
      fulfilled(state, action) {
        log?.debug('Creating item, response', action.payload);
        if (action.payload.clientId && state.ids.includes(action.payload.clientId)) {
          adapter.updateOne(state, {
            id: action.payload.clientId ?? action.payload.resource.clientId,
            changes: action.payload.resource,
          });
        } else {
          adapter.addOne(state, action.payload.resource);
        }
      },
      settled(state) {
        state.isLoading = false;
      },
    },
  ),
  fetch: create.asyncThunk(
    async (_: void) => {
      log?.debug('Getting items');
      return await api.fetch();
    },
    {
      pending(state) {
        state.isLoading = true;
      },
      fulfilled(state, action) {
        log?.debug('Getting items, response', action.payload);
        adapter.setAll(state, action.payload);
        state.error = undefined;
        state.hasRejectedMutation = false;
      },
      rejected(state, action) {
        log?.error('Received error', action.error);
        state.error = action.error;
      },
      settled(state) {
        state.isLoading = false;
        state.loadPending = false;
        state.hasCachedResult = false;
      },
    },
  ),
  update: create.asyncThunk(
    async (item: Partial<T> & { id: string }, thunkApi) => {
      log?.debug('Updating item', item);
      try {
        return await api.update(item);
      } catch (e) {
        refetch(thunkApi.dispatch);
        throw e;
      }
    },
    {
      pending(state, action) {
        state.isLoading = true;
        startMutation(state);
        adapter.updateOne(state, { id: getId(action.meta.arg), changes: action.meta.arg });
      },
      rejected(state, action) {
        log?.error('Received error', action.error);
        state.error = action.error;
        state.hasRejectedMutation = true;
      },
      fulfilled(state, action) {
        log?.debug('Updating item, response', action.payload);
        adapter.setOne(state, action.payload.resource);
      },
      settled(state) {
        state.isLoading = false;
        settleMutation(state);
      },
    },
  ),
  delete: create.asyncThunk(
    async (id: string, thunkApi) => {
      log?.debug('Deleting item', id);
      try {
        await api.delete(id);
      } catch (e) {
        refetch(thunkApi.dispatch);
        throw e;
      }
    },
    {
      pending(state, action) {
        state.isLoading = true;
        startMutation(state);
        adapter.removeOne(state, action.meta.arg);
      },
      fulfilled() {
        log?.debug('Delete item successful');
      },
      rejected(state, action) {
        log?.error('Received error', action.error);
        state.error = action.error;
        state.hasRejectedMutation = true;
      },
      settled(state) {
        state.isLoading = false;
        state.loadPending = false;
        settleMutation(state);
      },
    },
  ),
});

export const retryAsync = async <R>(asyncFunc: () => Promise<R>, retries = 3, delay?: number): Promise<R> => {
  try {
    return await asyncFunc();
  } catch (error) {
    if (retries === 1) throw error;
    if (delay) {
      await new Promise((res) => setTimeout(res, delay));
    }
    return await retryAsync(asyncFunc, retries - 1, delay);
  }
};
